/**
 * "Vue admin" — an admin-only switch in Réglages that makes the catalogue
 * show what families can't see yet: free stories scheduled for a later
 * drip date (each with a small date badge) and the packs retired from the
 * catalogue. The backend does the gating (`?include_scheduled=1` is
 * silently ignored for a non-admin), so this is purely a display choice.
 *
 * On by default for an admin, per device, in localStorage — same
 * try/catch pattern and pure-parse split as `installNudge.ts`, so it
 * degrades to the default under private browsing and stays unit testable
 * without jsdom. Turning it off gives back the exact family view.
 */
import { useAuth } from "./auth";
import type { StoryCard } from "./types";

const ADMIN_VIEW_PREF_KEY = "chouette.adminView.enabled";

/** `null` (nothing stored, or storage unavailable) defaults to on. */
export function parseAdminViewPref(raw: string | null): boolean {
  if (raw === null) return true;
  return raw === "1";
}

export function readAdminViewPref(): boolean {
  try {
    return parseAdminViewPref(localStorage.getItem(ADMIN_VIEW_PREF_KEY));
  } catch {
    return true;
  }
}

export function writeAdminViewPref(enabled: boolean): void {
  try {
    localStorage.setItem(ADMIN_VIEW_PREF_KEY, enabled ? "1" : "0");
  } catch {
    /* best effort — private browsing etc. */
  }
}

/** Is the admin view in force for the signed-in account on this device?
 *  Read once per render: pages refetch on mount, and the switch lives on
 *  another page (Réglages), so there's nothing to subscribe to. */
export function useAdminView(): boolean {
  const { me } = useAuth();
  return !!me?.is_admin && readAdminViewPref();
}

/** A free catalogue story families can't see yet — only ever served in the
 *  admin view. A story with no `published_at` at all is never published
 *  (not "always visible"), same rule as the backend's `is_published`. */
export function isScheduled(story: Pick<StoryCard, "published_at">, now: Date = new Date()): boolean {
  if (!story.published_at) return true;
  return new Date(story.published_at).getTime() > now.getTime();
}
